import html2canvas from 'html2canvas'

const A4 = {
  portrait: { width: 595.28, height: 841.89 },
  landscape: { width: 841.89, height: 595.28 }
}

const crcTable = makeCrcTable()

export function sanitizeFileName (name) {
  const value = String(name || 'file')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()

  return (value || 'file').slice(0, 120)
}

export async function downloadPdfForElements (elements, fileName, options) {
  const pdfBytes = await createPdfForElements(elements, options)
  downloadBlob(new Blob([pdfBytes], { type: 'application/pdf' }), ensureExt(fileName, '.pdf'))
}

export async function downloadPdfZipForElements (elements, fileNameGetter, zipName, options) {
  const pages = normalizeElements(elements)
  const entries = []
  const usedNames = {}

  for (let i = 0; i < pages.length; i++) {
    const itemName = typeof fileNameGetter === 'function'
      ? fileNameGetter(pages[i], i)
      : `document-${i + 1}`
    const safeName = uniqueFileName(ensureExt(sanitizeFileName(itemName), '.pdf'), usedNames)
    const pdfBytes = await createPdfForElements([pages[i]], options)
    entries.push({ name: safeName, data: pdfBytes })
  }

  const zipBytes = createZip(entries)
  downloadBlob(new Blob([zipBytes], { type: 'application/zip' }), ensureExt(zipName, '.zip'))
}

export async function createPdfForElements (elements, options) {
  const pages = normalizeElements(elements)
  const images = []

  for (let i = 0; i < pages.length; i++) {
    images.push(await renderElement(pages[i], options || {}))
  }

  return createPdf(images, options || {})
}

function normalizeElements (elements) {
  if (!elements) return []
  if (elements instanceof Element) return [elements]
  return Array.prototype.slice.call(elements).filter(Boolean)
}

async function renderElement (element, options) {
  await waitForElementAssets(element)

  const canvas = await html2canvas(element, {
    scale: options.scale || 2,
    useCORS: true,
    allowTaint: false,
    backgroundColor: '#ffffff',
    logging: false
  })

  return {
    width: canvas.width,
    height: canvas.height,
    data: dataUrlToBytes(canvas.toDataURL('image/jpeg', options.quality || 0.98))
  }
}

async function waitForElementAssets (element) {
  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready
  }

  const images = Array.prototype.slice.call(element.querySelectorAll('img'))
  await Promise.all(images.map((img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve()
    return new Promise((resolve) => {
      img.onload = resolve
      img.onerror = resolve
    })
  }))

  await new Promise((resolve) => requestAnimationFrame(resolve))
}

function createPdf (images, options) {
  const objects = []
  const kids = []
  let nextObject = 3

  objects[1] = pdfString('<< /Type /Catalog /Pages 2 0 R >>')

  images.forEach((image, index) => {
    const pageSize = resolvePageSize(image, options)
    const pageObject = nextObject++
    const contentObject = nextObject++
    const imageObject = nextObject++
    const imageName = `Im${index + 1}`
    const fit = fitImage(image, pageSize)
    const content = `q ${number(fit.width)} 0 0 ${number(fit.height)} ${number(fit.x)} ${number(fit.y)} cm /${imageName} Do Q`

    kids.push(`${pageObject} 0 R`)
    objects[pageObject] = pdfString(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageSize.width} ${pageSize.height}] /Resources << /XObject << /${imageName} ${imageObject} 0 R >> >> /Contents ${contentObject} 0 R >>`)
    objects[contentObject] = pdfStream(pdfString(content), `<< /Length ${pdfString(content).length} >>`)
    objects[imageObject] = pdfStream(
      image.data,
      `<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.data.length} >>`
    )
  })

  objects[2] = pdfString(`<< /Type /Pages /Count ${images.length} /Kids [${kids.join(' ')}] >>`)

  return buildPdf(objects)
}

function resolvePageSize (image, options) {
  const orientation = (options && options.orientation) || 'portrait'
  const baseSize = A4[orientation] || A4.portrait

  if (options && options.pageSize === 'content') {
    if (orientation === 'landscape') {
      return {
        width: baseSize.width,
        height: baseSize.width * image.height / image.width
      }
    }

    return {
      width: baseSize.width,
      height: baseSize.width * image.height / image.width
    }
  }

  return baseSize
}

function buildPdf (objects) {
  const chunks = [pdfString('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n')]
  const offsets = [0]
  let length = chunks[0].length

  for (let i = 1; i < objects.length; i++) {
    offsets[i] = length
    const objectBytes = objectToBytes(i, objects[i])
    chunks.push(objectBytes)
    length += objectBytes.length
  }

  const xrefOffset = length
  let xref = `xref\n0 ${objects.length}\n0000000000 65535 f \n`
  for (let i = 1; i < objects.length; i++) {
    xref += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`
  }
  xref += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`
  chunks.push(pdfString(xref))

  return concatBytes(chunks)
}

function objectToBytes (id, value) {
  if (value && value.stream) {
    return concatBytes([
      pdfString(`${id} 0 obj\n${value.dict}\nstream\n`),
      value.stream,
      pdfString('\nendstream\nendobj\n')
    ])
  }
  return pdfString(`${id} 0 obj\n${bytesToString(value)}\nendobj\n`)
}

function pdfStream (stream, dict) {
  return { stream, dict }
}

function fitImage (image, pageSize) {
  const pageRatio = pageSize.width / pageSize.height
  const imageRatio = image.width / image.height
  let width = pageSize.width
  let height = pageSize.height
  let x = 0
  let y = 0

  if (imageRatio > pageRatio) {
    height = pageSize.width / imageRatio
    y = (pageSize.height - height) / 2
  } else {
    width = pageSize.height * imageRatio
    x = (pageSize.width - width) / 2
  }

  return { width, height, x, y }
}

function createZip (entries) {
  const files = []
  const central = []
  let offset = 0
  const date = dosDateTime(new Date())

  entries.forEach((entry) => {
    const nameBytes = utf8(entry.name)
    const data = entry.data
    const crc = crc32(data)
    const local = new Uint8Array(30 + nameBytes.length)
    const localView = new DataView(local.buffer)

    writeU32(localView, 0, 0x04034b50)
    writeU16(localView, 4, 20)
    writeU16(localView, 6, 0x0800)
    writeU16(localView, 8, 0)
    writeU16(localView, 10, date.time)
    writeU16(localView, 12, date.date)
    writeU32(localView, 14, crc)
    writeU32(localView, 18, data.length)
    writeU32(localView, 22, data.length)
    writeU16(localView, 26, nameBytes.length)
    writeU16(localView, 28, 0)
    local.set(nameBytes, 30)

    files.push(local, data)
    central.push({ nameBytes, data, crc, offset })
    offset += local.length + data.length
  })

  const centralParts = []
  let centralLength = 0
  central.forEach((entry) => {
    const header = new Uint8Array(46 + entry.nameBytes.length)
    const view = new DataView(header.buffer)
    writeU32(view, 0, 0x02014b50)
    writeU16(view, 4, 20)
    writeU16(view, 6, 20)
    writeU16(view, 8, 0x0800)
    writeU16(view, 10, 0)
    writeU16(view, 12, date.time)
    writeU16(view, 14, date.date)
    writeU32(view, 16, entry.crc)
    writeU32(view, 20, entry.data.length)
    writeU32(view, 24, entry.data.length)
    writeU16(view, 28, entry.nameBytes.length)
    writeU16(view, 30, 0)
    writeU16(view, 32, 0)
    writeU16(view, 34, 0)
    writeU16(view, 36, 0)
    writeU32(view, 38, 0)
    writeU32(view, 42, entry.offset)
    header.set(entry.nameBytes, 46)
    centralParts.push(header)
    centralLength += header.length
  })

  const end = new Uint8Array(22)
  const endView = new DataView(end.buffer)
  writeU32(endView, 0, 0x06054b50)
  writeU16(endView, 4, 0)
  writeU16(endView, 6, 0)
  writeU16(endView, 8, entries.length)
  writeU16(endView, 10, entries.length)
  writeU32(endView, 12, centralLength)
  writeU32(endView, 16, offset)
  writeU16(endView, 20, 0)

  return concatBytes(files.concat(centralParts, [end]))
}

function dosDateTime (date) {
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
    date: ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate()
  }
}

function crc32 (bytes) {
  let crc = 0 ^ -1
  for (let i = 0; i < bytes.length; i++) {
    crc = (crc >>> 8) ^ crcTable[(crc ^ bytes[i]) & 0xff]
  }
  return (crc ^ -1) >>> 0
}

function makeCrcTable () {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let j = 0; j < 8; j++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[i] = c >>> 0
  }
  return table
}

function uniqueFileName (name, usedNames) {
  const safeName = sanitizeFileName(name)
  if (!usedNames[safeName]) {
    usedNames[safeName] = 1
    return safeName
  }

  usedNames[safeName] += 1
  const extIndex = safeName.lastIndexOf('.')
  if (extIndex === -1) return `${safeName}-${usedNames[safeName]}`
  return `${safeName.slice(0, extIndex)}-${usedNames[safeName]}${safeName.slice(extIndex)}`
}

function ensureExt (name, ext) {
  const safe = sanitizeFileName(name)
  return safe.toLowerCase().endsWith(ext) ? safe : `${safe}${ext}`
}

function dataUrlToBytes (dataUrl) {
  const base64 = dataUrl.split(',')[1] || ''
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

function downloadBlob (blob, fileName) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = sanitizeFileName(fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function concatBytes (parts) {
  const total = parts.reduce((sum, part) => sum + part.length, 0)
  const result = new Uint8Array(total)
  let offset = 0
  parts.forEach((part) => {
    result.set(part, offset)
    offset += part.length
  })
  return result
}

function pdfString (text) {
  const bytes = new Uint8Array(text.length)
  for (let i = 0; i < text.length; i++) {
    bytes[i] = text.charCodeAt(i) & 0xff
  }
  return bytes
}

function bytesToString (bytes) {
  if (typeof bytes === 'string') return bytes
  let text = ''
  for (let i = 0; i < bytes.length; i++) {
    text += String.fromCharCode(bytes[i])
  }
  return text
}

function utf8 (text) {
  return new TextEncoder().encode(text)
}

function number (value) {
  return Number(value).toFixed(3).replace(/\.?0+$/, '')
}

function writeU16 (view, offset, value) {
  view.setUint16(offset, value, true)
}

function writeU32 (view, offset, value) {
  view.setUint32(offset, value >>> 0, true)
}
